import {Box, Radio, Stack} from '@mantine/core';
import React from 'react';

import SearchInput from 'search/inputs/SearchInput';

import RoutineFieldHeader from '../RoutineFieldHeader';
import {EventTriggerConfig} from '../RoutineTypes';
import {withIndefiniteArticle} from '../routineUtils';
import {UNAVAILABLE_FEATURE_TOOLTIP} from '../triggerConstants';
import RoutineSchemaSelector from './RoutineSchemaSelector';
import {RoutineSchemaOption, RoutineSchemaSource} from './routineSchemaSources';
import SelectableRadioCard from './SelectableRadioCard';
import {TargetScopeCapabilities, TriggerScope} from './types';

interface TargetScopeSectionProps {
  capabilities: TargetScopeCapabilities;
  commonNounLabel: string;
  config: Partial<EventTriggerConfig>;
  getCardClassName: (selected: boolean, disabled: boolean) => string;
  pluralObjectTypeLabel: string;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  onScopeChange: (nextScope: TriggerScope) => void;
  scope: TriggerScope | undefined;
  searchInputContainerClassName: string;
  sectionClassName: string;
  selectDropdownClassName: string;
  selectedObjectSearchType: string;
  selectedObjectType: string;
  selectedSchemaSource: RoutineSchemaSource | null;
  schemaOptions: Array<RoutineSchemaOption>;
  schemasLoading: boolean;
  schemasError: boolean;
}

export default function TargetScopeSection({
  capabilities,
  commonNounLabel,
  config,
  getCardClassName,
  pluralObjectTypeLabel,
  onChange,
  onScopeChange,
  scope,
  searchInputContainerClassName,
  sectionClassName,
  selectDropdownClassName,
  selectedObjectSearchType,
  selectedObjectType,
  selectedSchemaSource,
  schemaOptions,
  schemasLoading,
  schemasError,
}: TargetScopeSectionProps): JSX.Element {
  const objectCreatedSpecificInstanceTooltip = `Unable to select a specific ${commonNounLabel} with this type of trigger`;

  return (
    <Stack spacing={4} className={sectionClassName}>
      <RoutineFieldHeader title="Targets" description="What is the scope for this Agent" />

      <Radio.Group value={scope ?? ''}>
        <Stack spacing="sm">
          <SelectableRadioCard
            value="all"
            label={`All ${pluralObjectTypeLabel}`}
            description={`Run when any ${commonNounLabel} matches the specified trigger`}
            disabled={false}
            showTooltip={false}
            onSelect={() => {
              onScopeChange('all');
            }}
            cardClassName={getCardClassName(scope === 'all', false)}
          />
          <SelectableRadioCard
            value="schema"
            label={`A type of ${commonNounLabel}`}
            description={`Specify the specific type of ${commonNounLabel} that would trigger this Agent`}
            disabled={capabilities.schemaScopeDisabled}
            showTooltip={capabilities.showSchemaScopeUnavailableTooltip}
            tooltipLabel={UNAVAILABLE_FEATURE_TOOLTIP}
            onSelect={() => {
              onScopeChange('schema');
            }}
            cardClassName={getCardClassName(scope === 'schema', capabilities.schemaScopeDisabled)}
          >
            {scope === 'schema' && selectedSchemaSource != null && (
              <Box className={searchInputContainerClassName}>
                <RoutineSchemaSelector
                  selectedSchemaId={config.schema_id ?? null}
                  onChange={onChange}
                  schemas={schemaOptions}
                  loading={schemasLoading}
                  error={schemasError}
                  dropdownClassName={selectDropdownClassName}
                />
              </Box>
            )}
          </SelectableRadioCard>
          <SelectableRadioCard
            value="instance"
            label={`A specific ${commonNounLabel}`}
            description={`Select the specific instance of ${withIndefiniteArticle(commonNounLabel)} that would trigger this Agent`}
            disabled={capabilities.instanceScopeDisabled}
            showTooltip={capabilities.showObjectCreatedInstanceTooltip}
            tooltipLabel={objectCreatedSpecificInstanceTooltip}
            onSelect={() => {
              onScopeChange('instance');
            }}
            cardClassName={getCardClassName(scope === 'instance', capabilities.instanceScopeDisabled)}
          >
            {scope === 'instance' && selectedObjectType !== '' && (
              <Box className={searchInputContainerClassName}>
                <SearchInput
                  types={[selectedObjectSearchType]}
                  selected={config.object_ids ?? []}
                  onSelect={(id) => {
                    if (id != null && !(config.object_ids ?? []).includes(id)) {
                      onChange({object_ids: [...(config.object_ids ?? []), id]});
                    }
                  }}
                  onRemove={(id) => {
                    onChange({
                      object_ids: (config.object_ids ?? []).filter((existingId) => existingId !== id),
                    });
                  }}
                  hideSelectedResults={true}
                  isMulti={true}
                  placeholder={`Search for ${commonNounLabel}...`}
                  dropLeft={true}
                />
              </Box>
            )}
          </SelectableRadioCard>
        </Stack>
      </Radio.Group>
    </Stack>
  );
}
